import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";

/**
 * Escalating recovery for a boot that never completes.
 *
 * `launchAutoHide` is false, so the white launch splash sits over the web view — with
 * `isUserInteractionEnabled = false` under it — until JS hides it. Every splash dismissal
 * lives behind code that can fail: `hideSplashOnce()` needs the first native route to
 * commit, App.tsx's 2s fallback needs the module graph to import and the first render to
 * commit. A bundle that throws at module scope (the `apiClients` incident that moved
 * `notifyAppReady` to first import), a persisted store whose rehydrate trips over state
 * written by a different build, or a swap onto a bundle that never renders, each leaves
 * the splash up on every launch. The bad state lives on disk — @capgo's bundle pointer in
 * the Library directory, the web view's own storage — so a force-quit changes nothing and
 * only a reinstall clears it. That is the constant white screen with no tab bar.
 *
 * The commonest way in is an OTA bundle on a binary too old for it; nativeCompat.ts
 * catches that on the first boot, without the wait or the storage wipe. This chain is
 * the net under everything else.
 *
 * This module arms itself second in index.tsx — after `notifyAppReady` (which must stay
 * first to keep @capgo's rollback honest) and before anything with a real chance of
 * throwing — so its listeners and watchdog exist no matter what fails later. It counts
 * consecutive failed boots in localStorage (which survives a reload, unlike module state,
 * and survives a force-quit, which is the point) and escalates one step per boot:
 *
 * 1. Reload. Covers a one-off: a half-written swap, a transient bridge failure.
 * 2. Clear localStorage and every IndexedDB, reload. Covers persisted state that trips
 *    the running bundle's rehydration.
 * 3. @capgo `reset()` back to the bundled web build. Covers a bad OTA bundle — which the
 *    rollback watchdog cannot, because `notifyAppReady` has already fired by the time
 *    anything past the first import crashes.
 * 4. Give up reloading and paint a static failure panel, so a user whose bundled boot is
 *    also broken sees an explanation and a support address instead of white.
 *
 * A healthy boot calls `markBootCommitted()` from `hideSplashOnce()` — the same moment
 * the first real view lifts the splash — which resets the counter and disarms everything.
 * On web the whole chain is inert: a white page there is refresh-recoverable already.
 */

const STAGE_KEY = "bootRecoveryStage";

/**
 * Long enough that even a cold-swapped slow boot commits first, and past
 * indexdbStorage's 20s read timeout, so a store read that legitimately gives up still
 * gets to render before it counts as a failure. Short enough to matter.
 */
export const BOOT_DEADLINE_MS = 25_000;

/** Stage at which reloading stops being the recovery and the panel takes over. */
const DEAD_STAGE = 4;

const KEPT_KEYS = new Set([STAGE_KEY, "REACT_APP_STAGE"]);

let committed = !Capacitor.isNativePlatform();
let escalating = false;

const readStage = (): number => {
  try {
    return Number(localStorage.getItem(STAGE_KEY)) || 0;
  } catch {
    return 0;
  }
};

const writeStage = (stage: number): void => {
  try {
    localStorage.setItem(STAGE_KEY, String(stage));
  } catch {
    // Private-mode storage: the chain degrades to one attempt per process, still safe.
  }
};

/** The first view committed: boot is healthy, forget any previous failures. */
export function markBootCommitted(): void {
  if (escalating) return;
  committed = true;
  try {
    localStorage.removeItem(STAGE_KEY);
  } catch {
    // As above.
  }
}

const hideSplash = async (): Promise<void> => {
  try {
    await SplashScreen.hide();
  } catch {
    // The splash owner failing must not stop the recovery behind it.
  }
};

const clearAppStorage = async (): Promise<void> => {
  try {
    for (const key of Object.keys(localStorage)) {
      if (!KEPT_KEYS.has(key)) localStorage.removeItem(key);
    }
  } catch {
    // Keep going into the IndexedDB sweep regardless.
  }
  try {
    const databases = await indexedDB.databases?.();
    for (const db of databases ?? []) {
      if (db.name) indexedDB.deleteDatabase(db.name);
    }
  } catch {
    // Older WebKit without databases(): the store keys live in localStorage anyway.
  }
};

const paintFailurePanel = (): void => {
  const host = document.getElementById("root") ?? document.body;
  host.innerHTML = `
    <div style="position:fixed;inset:0;background:#fff;color:#111;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;padding:32px;text-align:center;font-family:-apple-system,sans-serif;">
      <div style="font-size:20px;font-weight:600;">EventR couldn't start</div>
      <div style="font-size:15px;color:#444;max-width:28em;">
        The app failed to start ${DEAD_STAGE} times in a row and reset itself doing so.
        Force-quit it and open it again; if that keeps happening, reinstall the app.
      </div>
      <a href="mailto:contact-eventr@softpauer.com" style="font-size:15px;color:#444;">contact-eventr@softpauer.com</a>
    </div>`;
};

/**
 * One escalation step for a boot that will not complete. Idempotent per document: the
 * first failure wins the step, later listeners in the same doomed boot are noise.
 */
export const reportBootFailure = async (source: string): Promise<void> => {
  if (committed || escalating) return;
  escalating = true;

  const stage = readStage() + 1;
  writeStage(stage);
  console.error(`[bootSafety] boot did not complete (${source}); recovery stage ${stage}`);

  await hideSplash();

  if (stage >= DEAD_STAGE) {
    paintFailurePanel();
    return;
  }
  if (stage === 2) await clearAppStorage();
  if (stage === 3) {
    // Handing back to @capgo reloads the web view with the bundled build itself.
    try {
      await CapacitorUpdater.reset();
    } catch (error) {
      console.error("[bootSafety] reset to builtin failed, reloading anyway", error);
      window.location.reload();
    }
    return;
  }
  window.location.reload();
};

/**
 * Counts only time the app spends on screen. A background launch (a location wake, a
 * silent push) runs this bundle with nothing to show, and its first view may not commit
 * until the user opens the app; counting that time would burn a stage, and the storage
 * wipe at stage two, on a perfectly healthy boot.
 *
 * There is no `error` listener here on purpose. A module-scope throw anywhere behind this
 * import leaves nothing to commit the first view, so the watchdog catches it anyway, and
 * a listener would also fire on the benign uncaught errors a healthy boot can raise,
 * escalating every launch to a wiped store and the failure panel.
 */
const armWatchdog = (): void => {
  let remaining = BOOT_DEADLINE_MS;
  let startedAt = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const start = () => {
    if (timer || committed || escalating) return;
    startedAt = Date.now();
    timer = setTimeout(() => {
      void reportBootFailure(`watchdog: no first view within ${BOOT_DEADLINE_MS}ms on screen`);
    }, remaining);
  };
  const pause = () => {
    if (!timer) return;
    clearTimeout(timer);
    timer = undefined;
    remaining = Math.max(0, remaining - (Date.now() - startedAt));
  };

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") start();
    else pause();
  });
  if (document.visibilityState === "visible") start();
};

if (Capacitor.isNativePlatform()) armWatchdog();
